// @flow
import * as React from 'react';
import { Device } from '@ta/lithium.context';
import styles from './useBlockScroll.css';

type BlockScrollState = {
  instanceCount: number,
  scrollY: number | null,
  elementStyles: {
    documentHeight: string | null,
  },
  documentStyles: {
    position: string | null,
    top: string | null,
    width: string | null,
    overflow: string | null,
  },
};

const state: BlockScrollState = {
  instanceCount: 0,
  scrollY: null,
  elementStyles: {
    documentHeight: null,
  },
  documentStyles: {
    scrollY: null,
    position: null,
    top: null,
    width: null,
    documentHeight: null,
    overflow: null,
  },
};

type EnableBugFix = boolean;
type EnableBugFixWithTimeout = number;
type EnableBugFixWithTimeoutAndCallback = [number, () => mixed];

/** If true, then apply the iOS block scroll bug fix.
 *  If a number is provided, then apply the bug fix after that many miliseconds.
 *  If a tuple is provided, then apply the bug fix after that many miliseconds and execute the callback */
type UseBlockScroll = (
  enable?: boolean,
  enableIOSBugFix?: EnableBugFix | EnableBugFixWithTimeout | EnableBugFixWithTimeoutAndCallback,
) => mixed;

const saveDocumentState = isIOSSafari => {
  /* eslint-disable no-restricted-globals */
  if (
    !(
      typeof document !== 'undefined' &&
      document.body &&
      document.body.style &&
      document.documentElement &&
      document?.documentElement?.style
    )
  ) {
    return;
  }
  const documentBodyStyle = document.body.style;
  const documentElementStyle = document.documentElement.style;
  if (isIOSSafari) {
    state.scrollY = window.scrollY;
    state.documentStyles.position = documentBodyStyle.position;
    state.documentStyles.top = documentBodyStyle.top;
    state.documentStyles.width = documentBodyStyle.width;
    state.elementStyles.documentHeight = documentElementStyle.height;
  }
  state.documentStyles.overflow = documentBodyStyle.overflow;
};

const restoreDocumentState = () => {
  if (
    !(
      typeof document !== 'undefined' &&
      document.body &&
      document.body.style &&
      document.documentElement &&
      document?.documentElement?.style
    )
  ) {
    return;
  }
  const documentBodyStyle = document.body.style;
  const documentElementStyle = document.documentElement.style;
  const { documentStyles, elementStyles, scrollY } = state;
  // Overlay is closing and we're in the iOS11 bugfix case, so let's return to original position and original
  // scroll.
  // We purposely don't remove the .overlayiOS11bug class on unmount -- keeping it fixes a font issue that shows
  // on dismount.
  if (documentStyles.position) {
    documentBodyStyle.position = documentStyles.position;
  } else {
    documentBodyStyle.removeProperty('position');
  }
  if (documentStyles.top != null) {
    documentBodyStyle.top = documentStyles.top;
  } else {
    documentBodyStyle.removeProperty('top');
  }
  if (documentStyles.width) {
    documentBodyStyle.width = documentStyles.width;
  } else {
    documentBodyStyle.removeProperty('width');
  }

  if (documentStyles.overflow) {
    documentBodyStyle.overflow = documentStyles.overflow;
  } else {
    documentBodyStyle.removeProperty('overflow');
  }

  if (elementStyles.documentHeight || elementStyles.documentHeight === '') {
    documentElementStyle.height = elementStyles.documentHeight;
  } else {
    documentElementStyle.removeProperty('height');
  }
  // Scroll as the last side effect after position is reset
  if (scrollY !== null) {
    window.scrollTo(0, scrollY);
  }
};

const useBlockScroll: UseBlockScroll = (enable = true, enableIOSBugFix = true) => {
  const {
    os: { family },
  } = React.useContext(Device);
  const isIOSSafari = family === 'IOS';

  const applyIOSBugFix = React.useCallback(() => {
    if (
      !(
        typeof document !== 'undefined' &&
        document.body &&
        document.body.style &&
        document.documentElement &&
        document?.documentElement?.style
      )
    ) {
      return;
    }
    const documentBodyStyle = document.body.style;
    const documentElementStyle = document.documentElement.style;
    const documentBodyClassList = document.body.classList;

    // To handle the iOS 11 bug we must change body to use fixed positioning to avoid background scrolling.
    // We also memorize the scroll top so we can put the page back to how it was when the overlay closes.
    const { scrollY } = state;
    documentBodyStyle.position = 'fixed';
    if (scrollY) {
      documentBodyStyle.top = `${-1 * scrollY}px`;
    }
    documentBodyStyle.width = '100%';
    // For some reason, the height of the HTML element drops to zero.
    // Set it to 100vh to avoid issues with the IsVisible component
    documentElementStyle.height = '100vh';

    if (!documentBodyClassList.contains(styles.iOS11TextBug)) {
      documentBodyClassList.add(styles.iOS11TextBug);
    }
  }, []);

  // Flatten array
  const [timeout, callback] = Array.isArray(enableIOSBugFix)
    ? enableIOSBugFix
    : [enableIOSBugFix, undefined];

  const applyBlockScroll = React.useCallback(() => {
    if (!(typeof document !== 'undefined' && document.body && document.body.style)) {
      return () => {};
    }
    // save these off as non-null variables or flow will complain
    const documentBodyStyle = document.body.style;
    documentBodyStyle.overflow = 'hidden';
    if (isIOSSafari) {
      if (typeof timeout !== 'boolean') {
        const timeoutId = setTimeout(() => {
          applyIOSBugFix();
          if (callback) {
            callback();
          }
          return () => {
            clearTimeout(timeoutId);
          };
        }, timeout);
      } else if (timeout) {
        applyIOSBugFix();
      }
    }
    return () => {};
  }, [isIOSSafari, applyIOSBugFix, callback, timeout]);

  React.useEffect(() => {
    if (!enable) {
      return () => {};
    }
    state.instanceCount++;
    let cleanup = () => {};
    if (state.instanceCount === 1) {
      saveDocumentState(isIOSSafari);
      cleanup = applyBlockScroll();
    }
    return () => {
      cleanup();
      state.instanceCount--;
      if (state.instanceCount === 0) {
        restoreDocumentState();
      }
    };
  }, [enable, applyBlockScroll, isIOSSafari]);
};

export default useBlockScroll;
